
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import MessageDialog from "../common/MessageDialog";
import MessageAnalyticsDialog from "./MessageAnalyticsDialog";
import { AutomatedMessage, MessageFormValues } from "./message-utils/types";
import MessagesList from "./MessagesList";
import { useAutomatedMessages } from "@/hooks/useAutomatedMessages";

interface AutomatedMessagesProps {
  isModuleEnabled?: boolean;
  previewMode?: boolean;
}

const AutomatedMessages = ({ isModuleEnabled = true, previewMode = false }: AutomatedMessagesProps) => {
  const { 
    messages, 
    isLoading, 
    createMessage, 
    updateMessage, 
    deleteMessage, 
    toggleActive,
    isCreating,
    isUpdating,
    isDeleting,
    isToggling
  } = useAutomatedMessages('referrals');

  const [messageDialogOpen, setMessageDialogOpen] = useState<boolean>(false);
  const [analyticsDialogOpen, setAnalyticsDialogOpen] = useState<boolean>(false);
  const [currentMessage, setCurrentMessage] = useState<AutomatedMessage | null>(null);

  const handleAddMessage = () => {
    if (!isModuleEnabled) return;
    setCurrentMessage(null);
    setMessageDialogOpen(true);
  };

  const handleEditMessage = (message: AutomatedMessage) => {
    if (!isModuleEnabled) return;
    setCurrentMessage(message);
    setMessageDialogOpen(true);
  };

  const handleDeleteMessage = (id: string) => {
    if (!isModuleEnabled) return;
    deleteMessage(id);
  };

  const handleToggleActive = (id: string) => {
    if (!isModuleEnabled) return;
    toggleActive(id);
  };

  const handleOpenAnalytics = (message: AutomatedMessage) => {
    setCurrentMessage(message);
    setAnalyticsDialogOpen(true);
  };

  const handleSaveMessage = (values: MessageFormValues) => {
    if (!isModuleEnabled || !values.sendTime || !values.message.trim()) return;

    if (currentMessage) {
      updateMessage({ id: currentMessage.id, values });
    } else {
      createMessage(values);
    }

    setMessageDialogOpen(false);
  };

  const isDisabled = !isModuleEnabled;

  if (isLoading) {
    return (
      <Card className={isDisabled ? "opacity-60" : ""}>
        <CardHeader>
          <CardTitle>Notificações</CardTitle>
          <CardDescription>
            Configure as mensagens automáticas do seu programa de indicações
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32">
            <div className="text-gray-500">Carregando mensagens...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className={isDisabled ? "opacity-60" : ""}>
        <CardHeader>
          <CardTitle>Notificações</CardTitle>
          <CardDescription>
            Configure as mensagens automáticas do seu programa de indicações
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium">Mensagens automáticas</h3>
            <Button 
              onClick={handleAddMessage} 
              variant="brand"
              disabled={isDisabled || isCreating}
            >
              <Plus className="mr-2 h-4 w-4" /> 
              {isCreating ? "Criando..." : "Nova Mensagem"}
            </Button>
          </div>
          
          <MessagesList 
            messages={messages}
            onEditMessage={handleEditMessage}
            onDeleteMessage={handleDeleteMessage}
            onOpenAnalytics={handleOpenAnalytics}
            onToggleActive={handleToggleActive}
            isModuleEnabled={isModuleEnabled}
          />
        </CardContent>
      </Card>

      <MessageDialog 
        open={messageDialogOpen}
        onOpenChange={setMessageDialogOpen}
        onSave={handleSaveMessage}
        currentMessage={currentMessage}
        disabled={isDisabled || isCreating || isUpdating}
        moduleType="referrals"
      />

      <MessageAnalyticsDialog
        open={analyticsDialogOpen}
        onOpenChange={setAnalyticsDialogOpen}
        message={currentMessage}
      />
    </>
  );
};

export default AutomatedMessages;
